import React, { type JSX } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Paragraph, ParagraphData } from "./yamlPreviewTypes";

/** Addressees, plus the bank they sit behind. */
function RecipientsContent({ data }: { data: ParagraphData }): JSX.Element {
  return (
    <div className="space-y-2">
      {data.recipients &&
        data.recipients.map((recipient, idx) => (
          <div key={idx} className="text-gray-900">
            <div className="font-medium">{recipient.name}</div>
            {recipient.role && <div className="text-sm text-gray-600">{recipient.role}</div>}
          </div>
        ))}
      {data.bank && <div className="mt-2 text-gray-900">{data.bank}</div>}
    </div>
  );
}

/** Closing text followed by the signature block. */
function SignaturesContent({ data }: { data: ParagraphData }): JSX.Element {
  return (
    <div className="space-y-4">
      {data.closing_text && (
        <div className="text-gray-700 prose prose-sm max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.closing_text}</ReactMarkdown>
        </div>
      )}
      <div className="space-y-3 pt-3 border-t border-gray-200">
        {data.signers &&
          data.signers.map((signer, idx) => (
            <div key={idx} className="space-y-0.5">
              <div className="font-medium text-gray-900">{signer.name}</div>
              <div className="text-sm text-gray-600">{signer.title}</div>
            </div>
          ))}
        {data.on_behalf && (
          <div className="text-sm text-gray-600 italic mt-2">On behalf of {data.on_behalf}</div>
        )}
      </div>
    </div>
  );
}

/** Title and intro shared by both table paragraph types. */
function TableHeading({ data }: { data: ParagraphData }): JSX.Element {
  return (
    <>
      {data.title && <h4 className="font-semibold text-gray-900">{data.title}</h4>}
      {data.intro && <p className="text-sm text-gray-700">{data.intro}</p>}
    </>
  );
}

const TABLE_HEADER_CELL =
  "px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase";

/** Advisors listed by role and firm. */
function AdvisorsContent({ data }: { data: ParagraphData }): JSX.Element {
  return (
    <div className="space-y-3">
      <TableHeading data={data} />
      {data.advisors && data.advisors.length > 0 && (
        <div className="overflow-hidden border border-gray-300 rounded">
          <table className="min-w-full divide-y divide-gray-300 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className={TABLE_HEADER_CELL}>Role</th>
                <th className={TABLE_HEADER_CELL}>Firm</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.advisors.map((advisor, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2 text-gray-900">{advisor.item}</td>
                  <td className="px-3 py-2 text-gray-700">{advisor.firm}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Named contacts with mailto links. */
function ContactsContent({ data }: { data: ParagraphData }): JSX.Element {
  return (
    <div className="space-y-3">
      <TableHeading data={data} />
      {data.contacts && data.contacts.length > 0 && (
        <div className="overflow-hidden border border-gray-300 rounded">
          <table className="min-w-full divide-y divide-gray-300 text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className={TABLE_HEADER_CELL}>Name</th>
                <th className={TABLE_HEADER_CELL}>Role</th>
                <th className={TABLE_HEADER_CELL}>Email</th>
                <th className={TABLE_HEADER_CELL}>Phone</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.contacts.map((contact, idx) => (
                <tr key={idx}>
                  <td className="px-3 py-2 font-medium text-gray-900">{contact.name}</td>
                  <td className="px-3 py-2 text-gray-700">{contact.role}</td>
                  <td className="px-3 py-2 text-blue-600">
                    <a href={`mailto:${contact.email}`} className="hover:underline">
                      {contact.email}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{contact.phone}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const MARKDOWN_COMPONENTS = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="text-lg font-bold text-gray-900 mt-3 mb-2">{children}</h2>
  ),
  h3: ({ children }: { children?: React.ReactNode }) => (
    <h3 className="text-base font-bold text-gray-900 mt-2 mb-1">{children}</h3>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="text-gray-700 mb-2 leading-relaxed">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc list-inside mb-2 text-gray-700">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal list-inside mb-2 text-gray-700">{children}</ol>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-gray-900">{children}</strong>
  ),
  em: ({ children }: { children?: React.ReactNode }) => (
    <em className="italic text-gray-700">{children}</em>
  ),
  code: ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-gray-200 px-1 py-0.5 rounded text-xs font-mono text-gray-800">
        {children}
      </code>
    ) : (
      <code className="block bg-gray-200 p-2 rounded text-xs font-mono text-gray-800 overflow-x-auto">
        {children}
      </code>
    );
  },
};

/** Free-text paragraph rendered as markdown. */
function MarkdownContent({ data }: { data: string | ParagraphData }): JSX.Element {
  const paragraphData = typeof data === "string" ? data : String(data || "");

  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {paragraphData}
      </ReactMarkdown>
    </div>
  );
}

/** Renders one paragraph's body according to its declared type. */
export default function ParagraphContent({ paragraph }: { paragraph: Paragraph }): JSX.Element {
  const { type, data } = paragraph;

  if (type === "recipients") return <RecipientsContent data={data as ParagraphData} />;
  if (type === "signatures") return <SignaturesContent data={data as ParagraphData} />;
  if (type === "advisors-table") return <AdvisorsContent data={data as ParagraphData} />;
  if (type === "contacts-table") return <ContactsContent data={data as ParagraphData} />;

  return <MarkdownContent data={data} />;
}
